import React, { useCallback } from 'react';
import { Drawer, Stack } from '@mui/material';
import { useAccountData } from '@/hooks/useAccountData';
import { AccountDetailsFlyoutProps } from '../types';
import { FLYOUT_WIDTH } from '../constants';
import { FlyoutErrorBoundary } from '../components';
import FlyoutLoadingState from '../common/FlyoutLoadingState';
import FlyoutErrorState from '../common/FlyoutErrorState';
import AccountHeader from './AccountHeader';
import AccountContent from './AccountContent';

const AccountDetailsFlyout: React.FC<AccountDetailsFlyoutProps> = ({
  open,
  toggleDrawer,
  account,
  onDelete,
  onEdit,
  onMarkAsClosed,
  showClosedAccountBanner,
  tabsPanelKey,
  initialTabId,
  investorId,
}) => {
  const { dynamicFields, isLoading, hasError, error } = useAccountData(account);

  const handleClose = useCallback(() => {
    toggleDrawer(false);
  }, [toggleDrawer]);

  // Early return for no account
  if (!account) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <FlyoutLoadingState
        open={open}
        onClose={handleClose}
        message='Loading account details...'
        showProgress={false}
        drawerClassName='account-details-flyout'
        contentClassName='account-details-flyout-content'
      />
    );
  }

  // Error state
  if (hasError) {
    return (
      <FlyoutErrorState
        open={open}
        onClose={handleClose}
        error={error}
        message='Failed to load account details'
        title='Error Loading Account'
        subtitle='Account Details'
        onRetry={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <FlyoutErrorBoundary>
      <Drawer
        anchor='right'
        open={open}
        onClose={handleClose}
        variant='temporary'
        className='account-details-flyout'
      >
        <Stack
          sx={{
            width: FLYOUT_WIDTH,
            paddingTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            paddingBottom:
              'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          }}
          className='account-details-flyout-content'
        >
          <AccountHeader
            account={account}
            onClose={handleClose}
            onDelete={onDelete}
            onEdit={onEdit}
            onMarkAsClosed={onMarkAsClosed}
          />
          <AccountContent
            account={account}
            dynamicFields={dynamicFields}
            showClosedAccountBanner={showClosedAccountBanner}
            tabsPanelKey={tabsPanelKey}
            initialTabId={initialTabId}
            investorId={investorId}
          />
        </Stack>
      </Drawer>
    </FlyoutErrorBoundary>
  );
};

export default AccountDetailsFlyout;
